import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { farmAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import SubscriptionLock from '@/components/SubscriptionLock';
import ProduceForm, { CATEGORY_ICONS, produceAmount } from '@/components/ProduceForm';
import { hasFullAccess } from '@/lib/subscription';
import { categoryOf } from '@/data/produce';
import { Plus, Leaf, Calendar, Pencil, Trash2, Loader2 } from 'lucide-react';
import { formatPlotSize } from '@/lib/plotFormat';

/**
 * My Farm: "What I produce". Farmers don't think in plots — they have
 * land and grow or raise many things — so each entry is one thing they
 * produce, with its land area (crops) or how many (animals, poultry,
 * fish). Stored as plots so nutrition, weather and the home stats keep
 * working.
 */
export default function FarmPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  // The entry being edited, or null when adding.
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    loadPlots();
  }, [user.id]);

  const loadPlots = async () => {
    try {
      const res = await farmAPI.getPlots(user.id);
      setPlots(res.data || []);
    } catch (error) {
      console.error('Failed to load plots');
    } finally {
      setLoading(false);
    }
  };

  const openForm = (plot = null) => {
    setEditing(plot);
    setShowModal(true);
  };

  const handleSave = async (fields) => {
    setSaving(true);
    try {
      if (editing) {
        await farmAPI.updatePlot(editing.id, fields);
        toast({ title: t('farm.toast.plotUpdated') });
      } else {
        await farmAPI.createPlot({ ...fields, user_id: user.id, location: user.state || null });
        toast({ title: t('farm.toast.plotAdded') });
      }
      setShowModal(false);
      setEditing(null);
      loadPlots();
    } catch (error) {
      console.error('Save produce failed', error);
      toast({
        title: editing ? t('farm.toast.updateFailed') : t('farm.toast.plotFailed'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plotId) => {
    if (!window.confirm(t('farm.toast.confirmDelete'))) return;
    try {
      await farmAPI.deletePlot(plotId);
      toast({ title: t('farm.toast.plotDeleted') });
      loadPlots();
    } catch (error) {
      console.error('Delete plot failed', error);
      toast({ title: t('farm.toast.deleteFailed'), variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
      </div>
    );
  }

  // Check subscription for farmers
  const hasSubscription = hasFullAccess(user);
  if (!hasSubscription) {
    return (
      <SubscriptionLock
        bodyKey="subscriptionLock.body.farm"
        userType="farmer"
        benefitKeys={['farmMgmt', 'aiDiagnosis', 'weatherAlerts', 'marketPrices']}
      />
    );
  }

  const formatSize = (plot) => formatPlotSize(plot, t, i18n.language);

  return (
    <div className="p-4 md:p-6 lg:p-8 animate-fadeIn">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">{t('farm.title')}</h1>
          <p className="text-muted-foreground">{t('farm.subtitle')}</p>
        </div>
        <Button
          className="bg-farm-primary hover:bg-farm-primary-dark shrink-0"
          data-testid="add-plot-btn"
          onClick={() => openForm()}
        >
          <Plus className="w-4 h-4 mr-2" /> {t('farm.addPlot')}
        </Button>
      </div>

      <Dialog
        open={showModal}
        onOpenChange={(open) => {
          setShowModal(open);
          if (!open) setEditing(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? t('farm.editPlot') : t('farm.newPlot')}</DialogTitle>
          </DialogHeader>
          {/* keyed so the form starts fresh for each entry */}
          <ProduceForm
            key={editing?.id || 'new'}
            plot={editing}
            saving={saving}
            onSubmit={handleSave}
            onCancel={() => setShowModal(false)}
            submitLabel={editing ? t('farm.form.saveChanges') : t('farm.form.submit')}
          />
        </DialogContent>
      </Dialog>

      {plots.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Leaf className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">{t('farm.empty.title')}</h3>
            <p className="text-muted-foreground text-center mb-4">{t('farm.empty.subtitle')}</p>
            <Button onClick={() => openForm()} className="bg-farm-primary hover:bg-farm-primary-dark">
              <Plus className="w-4 h-4 mr-2" /> {t('farm.addFirstPlot')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plots.map((plot) => {
            const category = categoryOf(plot);
            const Icon = CATEGORY_ICONS[category] || Leaf;
            const size = formatSize(plot);
            return (
              <Card
                key={plot.id}
                className="hover:shadow-farm-lg transition-shadow"
                data-testid={`plot-card-${plot.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-12 h-12 bg-farm-primary/10 rounded-xl flex items-center justify-center shrink-0">
                        <Icon className="w-6 h-6 text-farm-primary" />
                      </div>
                      <div className="min-w-0">
                        <CardTitle className="text-lg break-words">{plot.crop_type}</CardTitle>
                        <p className="text-sm text-muted-foreground">{t(`produce.categories.${category}`)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openForm(plot)}
                        className="text-muted-foreground hover:text-farm-primary"
                        aria-label={t('farm.plot.editAria', { name: plot.crop_type })}
                        data-testid={`edit-plot-${plot.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(plot.id)}
                        className="text-muted-foreground hover:text-destructive"
                        aria-label={t('farm.plot.deleteAria', { name: plot.crop_type })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="text-sm">
                    <span className="font-medium">{produceAmount(plot, t, formatSize)}</span>
                    {category === 'crops' && size.secondary && (
                      <span className="text-muted-foreground"> · {size.secondary}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>{t('farm.plot.added', { date: new Date(plot.created_at).toLocaleDateString(i18n.language) })}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
